import { memo, useCallback, useId, useState } from 'react';

import { TriangleAlertIcon } from '@air/design-icons';
import { Popover } from '@air/primitive-popover';

import { workspaceAgentPanelPill } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';

/** Same capture-phase stop as `WorkspaceAgentReferencePill` so panel hotkeys do not run under the popover. */
const stopEscapeKeyPropagation = (event: KeyboardEvent) => {
  event.stopPropagation();
};

export type WorkspaceAgentUnknownMonikerPillProps = {
  monikerId: string;
  monikerType: string;
};

export const WorkspaceAgentUnknownMonikerPill = memo(
  ({ monikerId, monikerType }: WorkspaceAgentUnknownMonikerPillProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const titleId = useId();
    const bodyId = useId();

    const handleOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
    }, []);

    return (
      <span className="inline-flex max-w-full items-center align-middle leading-none">
        <Popover
          aria-describedby={bodyId}
          aria-labelledby={titleId}
          className="w-auto min-w-0 max-w-[280px] rounded-8 border ads:border-ads-gray-5 border-gray-5 ads:bg-raised bg-surface-1 p-4 font-normal ads:text-primary text-12 text-gray-12 shadow-popover"
          collisionPadding={8}
          onEscapeKeyDown={stopEscapeKeyPropagation}
          onOpenChange={handleOpenChange}
          open={isOpen}
          side="top"
          sideOffset={8}
          trigger={
            <button
              className={workspaceAgentPanelPill({ isInteractive: true, tone: 'warning' })}
              data-testid="WORKSPACE_AGENT_MESSAGE_UNKNOWN_MONIKER_PILL"
              title={`${monikerType}:${monikerId}`}
              type="button"
            >
              <TriangleAlertIcon aria-hidden className="shrink-0" size="xs" />
              <span className="truncate">Unknown item</span>
            </button>
          }
        >
          <p
            className="mb-1 font-semibold ads:text-primary text-12 text-gray-12"
            data-testid="WORKSPACE_AGENT_MESSAGE_UNKNOWN_MONIKER_POPOVER_TITLE"
            id={titleId}
          >
            Unknown item
          </p>
          <p
            className="font-normal ads:text-metadata text-12 text-gray-10"
            data-testid="WORKSPACE_AGENT_MESSAGE_UNKNOWN_MONIKER_POPOVER_BODY"
            id={bodyId}
          >
            This reference uses a type your app does not recognize yet. Refresh the page to load the latest version,
            then try again.
          </p>
        </Popover>
      </span>
    );
  },
);

WorkspaceAgentUnknownMonikerPill.displayName = 'WorkspaceAgentUnknownMonikerPill';
